import type { ReactNode } from 'react';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { CommandPalette } from '@/features/command-palette';

export type Page =
  | 'login'
  | 'dashboard'
  | 'doctors'
  | 'doctor-profile'
  | 'add-doctor'
  | 'edit-doctor'
  | 'documents'
  | 'invoices'
  | 'payments'
  | 'reports'
  | 'settings';

interface DashboardLayoutProps {
  children: ReactNode;
  onLogout?: () => void;
}

// LocalStorage key for sidebar collapsed state
const SIDEBAR_COLLAPSED_KEY = 'sidebar-collapsed';

export function DashboardLayout({ children, onLogout }: DashboardLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Initialize from localStorage
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return saved === 'true';
  });
  const location = useLocation();

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  // Derive current page from URL pathname - memoized
  const currentPage = useMemo((): string => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path.startsWith('/doctors')) return 'doctors';
    // Temporarily hidden - routes commented out
    // if (path.startsWith('/documents')) return 'documents';
    // if (path.startsWith('/invoices')) return 'invoices';
    // if (path.startsWith('/payments')) return 'payments';
    // if (path.startsWith('/reports')) return 'reports';
    // if (path.startsWith('/settings')) return 'settings';
    return 'dashboard';
  }, [location.pathname]);

  // Memoized callbacks to prevent creating new function references
  const handleOpenSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const handleCloseSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const handleToggleCollapse = useCallback(() => setIsSidebarCollapsed((prev) => !prev), []);

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 dark:bg-slate-950">
      <Sidebar
        currentPage={currentPage}
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={handleToggleCollapse}
        onLogout={onLogout}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header onMenuClick={handleOpenSidebar} />
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 p-2 dark:bg-slate-950 sm:p-3 lg:p-4">
          {children}
        </main>
      </div>
      {/* Global Command Palette - ⌘K to open */}
      <CommandPalette />
    </div>
  );
}
